import CardDataRow from '@/components/CardDataRow';
import CardModal from '@/components/CardModal';
import JoinOrganizationDialog from '@/components/JoinOrganizationDialog';
import RequestChangeEmailDialog from '@/components/RequestChangeEmailDialog';
import ThemeContext from '@/contexts/ThemeContext';
import { useLayoutStyles } from '@/hooks/styles';
import { logout } from '@/utils/reauthenticate';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AccountIcon from '@material-ui/icons/AccountBox';
import DarkModeIcon from '@material-ui/icons/Brightness4';
import Edit from '@material-ui/icons/Edit';
import JoinIcon from '@material-ui/icons/Login';
import LogoutIcon from '@material-ui/icons/PowerSettingsNew';
import SettingsIcon from '@material-ui/icons/Settings';
import { useRouter } from 'next/router';
import React, { useContext, useState } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    '& .MuiIconButton-root': {
      backgroundColor: 'transparent',
      padding: theme.spacing(1),
    },
  },
  icon: {
    color: theme.palette.action.active,
    marginRight: theme.spacing(2),
  },
}));

interface UserMenuProps {
  user?: {
    id: string;
    email: string;
  } | null;
}

export default function UserMenu({ user = null }: UserMenuProps): JSX.Element {
  const classes = useStyles();
  const layoutClasses = useLayoutStyles();
  const { currentTheme, setTheme } = useContext(ThemeContext);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const router = useRouter();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleJoinOrganization = () => {
    handleClose();
    setJoinOpen(true);
  };

  const handleToggleTheme = () => {
    handleClose();
    setTheme(currentTheme !== 'dark' ? 'dark' : 'light');
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    router.push('/signin');
  };

  const renderCommon = () => (
    <MenuItem onClick={handleToggleTheme}>
      <DarkModeIcon className={layoutClasses.menuIcon} />
      <span>
        {`${currentTheme !== 'dark' ? 'Enable' : 'Disable'} dark theme`}
      </span>
    </MenuItem>
  );

  const handleSettings = () => {
    handleClose();
    setShowSettingsModal(true);
  };

  return (
    <div className={classes.button}>
      <IconButton classes={{ root: classes.button }} onClick={handleClick}>
        {!user ? <SettingsIcon /> : <Avatar />}
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {!user ? (
          renderCommon()
        ) : (
          <Box>
            <MenuItem disabled>
              <AccountIcon className={layoutClasses.menuIcon} />
              {user.email}
            </MenuItem>
            <Divider className={layoutClasses.menuDivider} />
            <MenuItem onClick={handleSettings}>
              <SettingsIcon className={layoutClasses.menuIcon} />
              <span>Settings</span>
            </MenuItem>
            <MenuItem onClick={handleJoinOrganization}>
              <JoinIcon className={layoutClasses.menuIcon} />
              <span>Join organization</span>
            </MenuItem>
            {renderCommon()}
            <Divider className={layoutClasses.menuDivider} />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon className={layoutClasses.menuIcon} />
              <span>Logout</span>
            </MenuItem>
          </Box>
        )}
      </Menu>

      <CardModal
        showModal={showSettingsModal}
        setShowModal={setShowSettingsModal}
        title="User Settings"
      >
        <Box display="flex" flexDirection="row" alignItems="center" px={1}>
          <CardDataRow label="Email" value={user?.email} />
          <IconButton size="small" onClick={() => setShowEmailDialog(true)}>
            <Edit fontSize="inherit" />
          </IconButton>
        </Box>
      </CardModal>

      {showEmailDialog && (
        <RequestChangeEmailDialog
          open={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
        />
      )}

      <JoinOrganizationDialog
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onSuccess={({ pid }) => router.push(`/p/${pid}/profile/edit`)}
      />
    </div>
  );
}
